
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  ProfileMapInterface,
} from '@/services/QC-V2/chat/interfaces';
import { ScoreTypeInterface } from '@/services/QC-V2/score-type/interfaces';
import { ScoreGradeInterface } from '@/services/QC-V2/score-grade/interfaces';
import { TagInterface } from '@/services/QC-V2/tag/interfaces';
import { CommentRate } from '@/services/QC-V2/comment/interfaces';
import DossierFeedbackRepositoryInterface from '@/services/Dossier/feedback/interfaces';
import { TicketInterface } from '@/services/QC-V2/ticket/interfaces';
import {
  FeedbackRepositoryInterface,
  FeedbackStatus, TicketFeedbackCreateInterface,
} from '@/services/QC-V2/feedback/interfaces';
import {
  EscalationRepositoryInterface,
  TicketEscalationCreateData,
} from '@/services/QC-V2/escalation/interfaces';
import { UserBaseInfoInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import FeedbackDialog, { FeedbackDialogDataInterface } from '@/components/dialogs/qc-v2/feedback/FeedbackDialog.vue';
import TicketEscalateDialog from '@/components/dialogs/qc-v2/escalation/TicketEscalateDialog.vue';

interface ArchiveScoreInterface {
  scoreType: number;
  scoreGrade: number;
  reviewerId: string;
  deleted: boolean;
}

interface ArchiveTagInterface {
  tagId: number;
  deleted: boolean;
}

interface CommentInterface {
  id: number,
  author: string;
  timestamp: string;
  text: string;
  rate: CommentRate;
}

interface ArchiveReviewInterface {
  id: number;
  reviewedId: string;
  scores: ArchiveScoreInterface[];
  tags: ArchiveTagInterface[];
  comments: CommentInterface[];
}

@Component({
  components: {
    TicketEscalateDialog,
    FeedbackDialog,
  },
  mixins: [
    Themeable,
  ],
  inject: [
    'dossierFeedbackRepository',
    'feedbackRepositoryV2',
    'escalationRepositoryV2',
    'userRepository',
  ],
})
export default class TicketReviewsDetails extends Vue {
  @Prop() private ticket!: TicketInterface;
  @Prop() private scoreTypes!: ScoreTypeInterface[];
  @Prop() private scoreGrades!: ScoreGradeInterface[];
  @Prop() private tags!: TagInterface[];
  @Prop() private profilesMap!: ProfileMapInterface;

  private dossierFeedbackRepository!: DossierFeedbackRepositoryInterface;
  private feedbackRepositoryV2!: FeedbackRepositoryInterface;
  private escalationRepositoryV2!: EscalationRepositoryInterface;
  private userRepository!: UserRepoInterface;

  private scoreTypesMap: { [key: number]: string } = {};
  private scoreGradesMap: { [key: number]: string } = {};
  private tagsMap: { [key: number]: TagInterface } = {};

  private collapsed: boolean = false;

  private reviews: ArchiveReviewInterface[] = [];

  private dataForFeedbackDialog: FeedbackDialogDataInterface | undefined = undefined;
  private feedbackDialogVisible: boolean = false;

  private escalateDialogVisible: boolean = false;

  private users: UserBaseInfoInterface[] = [];

  private created(): void {
    this.createScoresMap();
    this.createTagsMap();
    this.aggregateReviews();
  }

  @Watch('ticket', { deep: true })
  private handleChatUpdate(): void {
    this.reviews = [];
    this.aggregateReviews();
    this.$forceUpdate();
  }

  private createScoresMap(): void {
    this.scoreTypes.forEach((scoreType) => {
      this.scoreTypesMap[scoreType.id] = scoreType.label;
    });

    this.scoreGrades.forEach((scoreGrade) => {
      this.scoreGradesMap[scoreGrade.id] = scoreGrade.label;
    });
  }

  private createTagsMap(): void {
    this.tags.forEach((tag) => {
      this.tagsMap[tag.id] = tag;
    });
  }

  private aggregateReviews(): void {
    this.ticket.reviews.forEach((review) => {
      let index = this.reviews.findIndex((rev) => rev.reviewedId === review.reviewedId);
      if (index === -1) {
        this.reviews.push({
          id: review.id,
          reviewedId: review.reviewedId,
          scores: [],
          tags: [],
          comments: [],
        });

        index = this.reviews.length - 1;
      }

      this.reviews[index].scores.push(
        ...review.scores.map((score) => ({
          reviewerId: review.reviewerId,
          scoreType: score.scoreTypeId,
          scoreGrade: score.scoreGradeId,
          deleted: !!review.deletedAt,
        })),
      );

      this.reviews[index].scores = this.reviews[index].scores
        .sort((scoreA, scoreB) => (scoreA.deleted === scoreB.deleted ? 0 : scoreA ? 1 : -1));

      this.reviews[index].tags.push(
        ...review.tags.map((tag) => ({
          tagId: tag.tagId,
          deleted: !!review.deletedAt,
        })),
      );

      this.reviews[index].tags = this.reviews[index].tags
        .sort((tagA, tagB) => tagA.deleted === tagB.deleted ? 0 : tagA ? 1 : -1);

      this.reviews[index].comments.push(
        ...review.comments.map((comment) => ({
          id: comment.id,
          author: comment.author,
          text: comment.text,
          timestamp: String(comment.timestamp),
          rate: comment.rate,
        })),
      );

      this.reviews[index].comments = this.reviews[index].comments
        .sort((commentA, commentB) => new Date(commentA.timestamp).getTime()
          - new Date(commentB.timestamp).getTime());
    });
  }

  private async sendToDossier(ticketNumber: number, review: ArchiveReviewInterface, commentId: number): Promise<void> {
    try {
      await this.dossierFeedbackRepository.createTicketComment({
        ticketNumber,
        commentId,
        reviewId: review.id,
        to: review.reviewedId,
        from: this.$store.getters.id,
      });
      this.$successDialog.show('Comment successfully send to dossier');
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || e.message);
    }
  }

  private openFeedbackDialog(reviewIndex: number): void {
    console.log(reviewIndex);
    const review = this.ticket.reviews[reviewIndex];

    const scores: string[] = [];

    review.scores.forEach((score) => {
      const type = this.scoreTypes.find((st) => st.id === score.scoreTypeId);
      const grade = this.scoreGrades.find((sg) => sg.id === score.scoreGradeId);

      if (!type || !grade) {
        return;
      }

      scores.push(`${type.label} - ${grade.label}`);
    });

    const tags: TagInterface[] = [];
    review.tags.forEach((rt) => {
      const tag = this.tags.find((t) => t.id === rt.tagId);
      if (!tag) {
        return;
      }

      tags.push(tag);
    });

    this.dataForFeedbackDialog = {
      user: {
        id: review.reviewedId,
        name: `${this.profilesMap[review.reviewedId].firstName} ${this.profilesMap[review.reviewedId].lastName}`,
      },
      comments: review.comments,
      scores,
      tags,
      usersMap: this.profilesMap,
      reviewIndex,
    };

    this.feedbackDialogVisible = true;
  }

  private async createFeedback(reviewIndex?: number): Promise<void> {
    if (!reviewIndex && reviewIndex !== 0) {
      this.$errorDialog.show('Missing review for feedback!');
      return;
    }

    const review = this.ticket.reviews[reviewIndex];
    if (!review) {
      this.$errorDialog.show('Missing review for feedback!');
      return;
    }

    const feedbackData: TicketFeedbackCreateInterface = {
      isDraft: false,
      ticketId: this.ticket.id,
      feedbackTo: review.reviewedId,
      feedbackFrom: this.$store.getters.id,
      status: FeedbackStatus.SENT,
    };

    const scores: { [key: number]: number } = {};
    review.scores.forEach((score) => {
      scores[score.scoreTypeId] = score.scoreGradeId;
    });

    const tags: TagInterface[] = [];
    review.tags.forEach((rt) => {
      const tag = this.tags.find((t) => t.id === rt.tagId);
      if (!tag) {
        return;
      }

      tags.push(tag);
    });

    try {
      await this.feedbackRepositoryV2.createTicketFeedback(
        feedbackData,
        {
          ticketId: this.ticket.id,
          reviewedId: review.reviewedId,
          reviewerId: this.$store.getters.id,
          comments: review.comments,
          isDraft: false,
          scores,
          tags,
        },
      );
      this.ticket.feedbacks.push({
        id: 0,
        feedbackTo: feedbackData.feedbackTo,
        feedbackFrom: feedbackData.feedbackFrom,
        status: FeedbackStatus.SENT,
        timestamp: new Date(),
      });
      this.$successDialog.show('Feedback created Successfully');
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private openEscalationDialog(): void {
    this.escalateDialogVisible = true;
  }

  private async createEscalation(data: TicketEscalationCreateData): Promise<void> {
    if (this.ticket.escalation) {
      this.$successDialog.show('Ticket already escalated');
      return;
    }
    try {

      data.agentsIds = this.users.map((user) => user.id);
      await this.escalationRepositoryV2.createTicketEscalation(data);
      this.ticket.escalation = {
        ticket: this.ticket,
        escalatedFrom: data.escalatedFrom,
        departmentManager: data.departmentManager || '',
        shiftManager: data.shiftManager || '',
        reason: data.reason,
        status: data.status,
        id: 0,
        priority: data.priority,
        timestamp: new Date().toISOString(),
      };
      this.$successDialog.show('Escalation created Successfully');
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private async fetchUsers(): Promise<void> {
    const emails = this.ticket.agents.map((agent) => agent.email);
    this.users = await this.userRepository.getUsersByEmails(emails);
  }
}
